export type CrossTextReviewStatus = 'pending' | 'accepted' | 'rejected' | 'deferred'

export type CrossTextAlignmentTier = 'tier_1_close' | 'tier_2_rewrite' | 'tier_3_substantial'

export interface CrossTextFolioMarker {
  marker: string
}

export interface CrossTextSourceSegment {
  index: number
  text: string
  folioMarkersBefore?: CrossTextFolioMarker[]
}

export interface CrossTextSourceText {
  textId: string
  label: string
  contextLabel?: string | null
  segments: CrossTextSourceSegment[]
}

export interface CrossTextAlignmentCandidate {
  candidateId: string
  sourceTextId: string
  sourceSegmentStart: number
  sourceSegmentEnd: number
  sourceText: string
  targetTextId: string
  targetLabel: string
  targetContextLabel?: string | null
  targetSegmentStart: number
  targetSegmentEnd: number
  targetText: string
  targetAlignmentStart: number | null
  targetAlignmentEnd: number | null
  score: number
  scoreMargin: number
  rank: number
  tier: CrossTextAlignmentTier
  perspectiveMode: string
  reviewStatus: CrossTextReviewStatus
  rerankModelId?: string | null
  rerankVerdict?: string | null
  rerankConfidence?: number | null
  rerankReason?: string | null
  duplicateCandidateIds?: string[]
}

export interface CrossTextAlignmentRun {
  previewId: string
  modelId: string
  rerankModelId?: string | null
  sourceText: CrossTextSourceText
  candidates: CrossTextAlignmentCandidate[]
  diagnostics?: Record<string, unknown>
}

export interface CrossTextReviewSummary {
  total: number
  pending: number
  accepted: number
  rejected: number
  deferred: number
}

export type CrossTextSourceSegmentState = CrossTextReviewStatus | 'mixed' | 'none'

const crossTextReviewStatusRank: Record<CrossTextReviewStatus, number> = {
  accepted: 0,
  pending: 1,
  deferred: 2,
  rejected: 3,
}

const crossTextTierRank: Record<CrossTextAlignmentTier, number> = {
  tier_1_close: 0,
  tier_2_rewrite: 1,
  tier_3_substantial: 2,
}

const sortCrossTextCandidates = (
  candidates: CrossTextAlignmentCandidate[],
): CrossTextAlignmentCandidate[] => (
  candidates.slice().sort((left, right) => {
    if (left.reviewStatus !== right.reviewStatus) {
      return crossTextReviewStatusRank[left.reviewStatus] - crossTextReviewStatusRank[right.reviewStatus]
    }
    if (left.tier !== right.tier) {
      return crossTextTierRank[left.tier] - crossTextTierRank[right.tier]
    }
    if (left.score !== right.score) {
      return right.score - left.score
    }
    if (left.rank !== right.rank) {
      return left.rank - right.rank
    }
    return left.candidateId.localeCompare(right.candidateId)
  })
)

const targetRangesOverlap = (
  left: CrossTextAlignmentCandidate,
  right: CrossTextAlignmentCandidate,
): boolean => (
  left.targetSegmentStart <= right.targetSegmentEnd
  && right.targetSegmentStart <= left.targetSegmentEnd
)

const areWindowVariants = (
  left: CrossTextAlignmentCandidate,
  right: CrossTextAlignmentCandidate,
): boolean => (
  left.targetTextId === right.targetTextId
  && targetRangesOverlap(left, right)
)

const mergeCandidateIds = (
  left: CrossTextAlignmentCandidate,
  right: CrossTextAlignmentCandidate,
): string[] => Array.from(new Set([
  ...(left.duplicateCandidateIds || [left.candidateId]),
  ...(right.duplicateCandidateIds || [right.candidateId]),
]))

const collapseCrossTextWindowVariants = (
  candidates: CrossTextAlignmentCandidate[],
): CrossTextAlignmentCandidate[] => {
  const collapsed: CrossTextAlignmentCandidate[] = []
  candidates.forEach(candidate => {
    const existingIndex = collapsed.findIndex(existing => areWindowVariants(existing, candidate))
    if (existingIndex < 0) {
      collapsed.push({ ...candidate, duplicateCandidateIds: [candidate.candidateId] })
      return
    }
    const existing = collapsed[existingIndex]
    collapsed[existingIndex] = {
      ...existing,
      duplicateCandidateIds: mergeCandidateIds(existing, candidate),
    }
  })
  return collapsed
}

export const getCrossTextCandidatesForSegment = (
  run: CrossTextAlignmentRun | null,
  segmentIndex: number,
  options: { includeRejected?: boolean; collapseWindowVariants?: boolean } = {},
): CrossTextAlignmentCandidate[] => {
  if (!run) {
    return []
  }
  const candidates = run.candidates
    .filter(candidate => (
      candidate.sourceSegmentStart <= segmentIndex &&
      segmentIndex <= candidate.sourceSegmentEnd &&
      (options.includeRejected || candidate.reviewStatus !== 'rejected')
    ))
  const sorted = sortCrossTextCandidates(candidates)
  return options.collapseWindowVariants ? collapseCrossTextWindowVariants(sorted) : sorted
}

export const updateCrossTextCandidateStatuses = (
  run: CrossTextAlignmentRun,
  candidateIds: string[],
  reviewStatus: CrossTextReviewStatus,
): CrossTextAlignmentRun => {
  const candidateIdSet = new Set(candidateIds)
  return {
    ...run,
    candidates: run.candidates.map(candidate => (
      candidateIdSet.has(candidate.candidateId)
        ? { ...candidate, reviewStatus }
        : candidate
    )),
  }
}

export const updateCrossTextCandidateStatus = (
  run: CrossTextAlignmentRun,
  candidateId: string,
  reviewStatus: CrossTextReviewStatus,
): CrossTextAlignmentRun => updateCrossTextCandidateStatuses(run, [candidateId], reviewStatus)

export const getCrossTextReviewSummary = (run: CrossTextAlignmentRun | null): CrossTextReviewSummary => {
  const summary: CrossTextReviewSummary = {
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    deferred: 0,
  }
  if (!run) {
    return summary
  }
  run.candidates.forEach(candidate => {
    summary.total += 1
    summary[candidate.reviewStatus] += 1
  })
  return summary
}

export const getCrossTextSourceSegmentState = (
  run: CrossTextAlignmentRun | null,
  segmentIndex: number,
): CrossTextSourceSegmentState => {
  const candidates = getCrossTextCandidatesForSegment(run, segmentIndex, { includeRejected: true })
  if (candidates.length === 0) {
    return 'none'
  }
  const activeCandidates = candidates.filter(candidate => candidate.reviewStatus !== 'rejected')
  if (activeCandidates.some(candidate => candidate.reviewStatus === 'accepted')) {
    return 'accepted'
  }
  if (activeCandidates.some(candidate => candidate.reviewStatus === 'pending')) {
    return 'pending'
  }
  if (activeCandidates.some(candidate => candidate.reviewStatus === 'deferred')) {
    return 'deferred'
  }
  return 'rejected'
}

export const getCrossTextTierLabel = (tier: CrossTextAlignmentTier): string => {
  switch (tier) {
    case 'tier_1_close':
      return 'Close paraphrase'
    case 'tier_2_rewrite':
      return 'Clear rewrite'
    case 'tier_3_substantial':
      return 'Substantial rewrite'
    default:
      return tier
  }
}
